import { useSyncExternalStore } from "react"
import type { CurrencyProblem } from "./accountCurrency.ts"
import type { StrategyResult } from "./protocol.ts"

// The latest backtest of each pane, for the Strategy Tester panel.

export interface PaneBacktest {
  /** A run is in progress; `result` is still the previous one. */
  readonly running: boolean
  /** The latest successful run. */
  readonly result: StrategyResult | null
  /** Open time (epoch seconds) of each bar of that run, aligned with `report.equity`. */
  readonly barTimes: ReadonlyArray<number>
  /** Why the latest run failed. */
  readonly error: string | null
  /** The latest run couldn't be converted to the account currency (no rates for some bars), and how to fix it. */
  readonly currencyProblem: CurrencyProblem | null
  /**
   * The strategy's latest successful run on any series: its title, inputs, and
   * default properties, for the settings dialog. Kept across symbol and timeframe
   * changes, so a run that fails on the new series can still be fixed.
   */
  readonly strategyInfo: { readonly strategyId: string; readonly result: StrategyResult } | null
}

const IDLE: PaneBacktest = { running: false, result: null, barTimes: [], error: null, currencyProblem: null, strategyInfo: null }

/** The settings-dialog view of a pane's strategy, if its info belongs to `strategyId`. */
export const strategyInfoFor = (backtest: PaneBacktest | undefined, strategyId: string): StrategyResult | null =>
  backtest?.strategyInfo?.strategyId === strategyId ? backtest.strategyInfo.result : null

let state: ReadonlyMap<number, PaneBacktest> = new Map()
const listeners = new Set<() => void>()
/** Scrolls a pane's chart to a time (List of Trades row clicks). */
const focusHandlers = new Map<number, (time: number) => void>()

const emit = () => {
  for (const listener of listeners) listener()
}

export const strategyResults = {
  update: (pane: number, patch: Partial<PaneBacktest>): void => {
    state = new Map(state).set(pane, { ...(state.get(pane) ?? IDLE), ...patch })
    emit()
  },
  clear: (pane: number): void => {
    if (!state.has(pane)) return
    const next = new Map(state)
    next.delete(pane)
    state = next
    emit()
  },
  setFocusHandler: (pane: number, handler: ((time: number) => void) | null): void => {
    if (handler === null) focusHandlers.delete(pane)
    else focusHandlers.set(pane, handler)
  },
  /** `time` in epoch seconds. */
  focus: (pane: number, time: number): void => focusHandlers.get(pane)?.(time)
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const usePaneBacktest = (pane: number): PaneBacktest | undefined =>
  useSyncExternalStore(subscribe, () => state.get(pane))
